import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import Cropper from "react-easy-crop";
import {
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaAngleLeft,
    FaAngleRight,
    FaEdit,
    FaInfoCircle,
    FaPlus,
    FaSearch,
    FaTimes,
    FaTrash,
    FaUpload,
} from "react-icons/fa";
import Swal from "sweetalert2";

import getCroppedImg from "../utils/cropImageUtils";

import { notifyUser } from "../utils/notifyUser";

const emptyPartner = {
    affiliateName: "",
    description: "",
    image: "",
    status: "published",
    websiteLink: "",
};

const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/affiliate-partners`;
const AFFILIATE_IMAGE_SIZE = {
    width: 1000,
    height: 1000,
};
const AFFILIATE_IMAGE_ASPECT =
    AFFILIATE_IMAGE_SIZE.width / AFFILIATE_IMAGE_SIZE.height;
const AFFILIATE_IMAGE_SIZE_LABEL = `${AFFILIATE_IMAGE_SIZE.width} x ${AFFILIATE_IMAGE_SIZE.height}px`;

const renderImageSizeTooltip = (size) => (
    <span className="group relative inline-flex items-center">
        <FaInfoCircle
            className="text-navy"
            tabIndex={0}
            aria-label={`Recommended size: ${size}`}
        />
        <span className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block">
            Recommended size: {size}
        </span>
    </span>
);

const getPreviewUrl = (image) => {
    if(!image) {
        return "";
    }

    if(image.startsWith("/uploads/")) {
        return `${process.env.REACT_APP_BACKEND_URL}${image}`;
    }

    if(image.startsWith("/api/images/")) {
        return image.replace(/^\/api\/images\//, "/images/");
    }

    return image;
};

const AffiliatePartnerManagement = () => {
    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [previewPartner, setPreviewPartner] = useState(null);
    const [form, setForm] = useState(emptyPartner);
    const [searchTerm, setSearchTerm] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [cropImage, setCropImage] = useState("");
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [pendingImageFileName, setPendingImageFileName] = useState("");

    const filteredPartners = useMemo(() => {
        const query = searchTerm.toLowerCase();
        return partners.filter(
            (partner) =>
                partner.affiliateName?.toLowerCase().includes(query) ||
                partner.description?.toLowerCase().includes(query) ||
                partner.websiteLink?.toLowerCase().includes(query),
        );
    }, [partners, searchTerm]);

    const loadPartners = async () => {
        setLoading(true);
        try {
            const response = await axios.get(apiUrl, { withCredentials: true });
            setPartners(sortByAuditUpdatedOn(response.data.partners || []));
        } catch{
            notifyUser(null, "error", "Unable to load affiliate partners", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPartners();
    }, []);

    const totalRecords = filteredPartners.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / perPage));
    const paginatedPartners = filteredPartners.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );
    const fromRecord = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(currentPage * perPage, totalRecords);

    useEffect(() => {
        if(currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleExportPartnersCSV = () => {
        if(!filteredPartners.length) {
            notifyUser(null, "info", "No affiliate partners to export", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const rows = filteredPartners.map((partner) => ({
            AffiliateName: partner.affiliateName || "",
            Website: partner.websiteLink || "",
            Status: partner.status || "",
            Description: partner.description || "",
        }));
        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((row) =>
                header
                    .map(
                        (key) =>
                            `"${String(row[key]).replace(/"/g, '""')}"`,
                    )
                    .join(","),
            ),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute(
            "download",
            `affiliate-partners-${new Date().toISOString().slice(0, 10)}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {fromRecord} to {toRecord} of {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                    aria-label="First page"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((page) => page - 1)}
                    className="disabled:opacity-40"
                    aria-label="Previous page"
                >
                    <FaAngleLeft />
                </button>
                <span>
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((page) => page + 1)}
                    className="disabled:opacity-40"
                    aria-label="Next page"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                    aria-label="Last page"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    const resetForm = () => {
        setSelectedPartner(null);
        setForm(emptyPartner);
    };

    const openNewPartnerModal = () => {
        resetForm();
        setIsEditorOpen(true);
    };

    const closeEditorModal = () => {
        setIsEditorOpen(false);
        resetForm();
    };

    const handleSelect = (partner) => {
        setSelectedPartner(partner);
        setForm({
            affiliateName: partner.affiliateName || "",
            description: partner.description || "",
            image: partner.image || "",
            status: partner.status === "draft" ? "draft" : "published",
            websiteLink: partner.websiteLink || "",
        });
        setIsEditorOpen(true);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const uploadImageFile = async (file) => {
        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append("image", file);

            const response = await axios.post(`${apiUrl}/image`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                withCredentials: true,
            });

            setForm((prev) => ({ ...prev, image: response.data.image }));
            notifyUser(null, "success", "Image uploaded", "toast", null, null, null, null, null, false, false, "top-center");
        } catch(error) {
            notifyUser(null, "error", error.response?.data?.error || "Unable to upload image", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setUploadingImage(false);
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];

        if(!file) {
            return;
        }

        if(!file.type.startsWith("image/")) {
            notifyUser(null, "error", "Only image files are allowed", "toast", null, null, null, null, null, false, false, "top-center");
            event.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setCropImage(reader.result);
            setPendingImageFileName(file.name || "affiliate-partner.jpg");
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
        };
        reader.readAsDataURL(file);
        event.target.value = "";
    };

    const handleCropCancel = () => {
        setCropImage("");
        setPendingImageFileName("");
        setCroppedAreaPixels(null);
    };

    const handleCropSave = async () => {
        if(!cropImage || !croppedAreaPixels) {
            return;
        }

        try {
            const croppedBlob = await getCroppedImg(
                cropImage,
                croppedAreaPixels,
                AFFILIATE_IMAGE_SIZE,
            );
            const baseName =
                pendingImageFileName.replace(/\.[^/.]+$/, "") ||
                "affiliate-partner";
            const croppedFile = new File([croppedBlob], `${baseName}-crop.jpg`, {
                type: "image/jpeg",
            });

            handleCropCancel();
            await uploadImageFile(croppedFile);
        } catch(error) {
            console.error("Affiliate image crop failed:", error);
            notifyUser(null, "error", "Failed to crop image", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleSave = async () => {
        if(!form.affiliateName.trim()) {
            notifyUser(null, "error", "Affiliate name is required", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(!form.description.trim()) {
            notifyUser(null, "error", "Description is required", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(!form.websiteLink.trim()) {
            notifyUser(null, "error", "Website link is required", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setSaving(true);

        try {
            const payload = {
                ...form,
                status: form.status === "draft" ? "draft" : "published",
                order: selectedPartner?.order || partners.length + 1,
            };

            if(selectedPartner?.id) {
                await axios.put(`${apiUrl}/${selectedPartner.id}`, payload, {
                    withCredentials: true,
                });
            } else {
                await axios.post(apiUrl, payload, { withCredentials: true });
            }

            notifyUser(null, "success", "Affiliate partner saved", "toast", null, null, null, null, null, false, false, "top-center");
            await loadPartners();
            closeEditorModal();
        } catch(error) {
            notifyUser(null, "error", error.response?.data?.error || "Unable to save affiliate partner", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (partner) => {
        const result = await Swal.fire({
            title: "Delete affiliate partner?",
            text: `This will remove "${partner.affiliateName}" from the site.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "var(--navy)",
            confirmButtonText: "Delete",
        });

        if(!result.isConfirmed) {
            return;
        }

        try {
            await axios.delete(`${apiUrl}/${partner.id}`, {
                withCredentials: true,
            });
            notifyUser(null, "success", "Affiliate partner deleted", "toast", null, null, null, null, null, false, false, "top-center");
            await loadPartners();
        } catch{
            notifyUser(null, "error", "Unable to delete affiliate partner", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <div className="aqi-grid-page w-[95%] mx-auto my-8 bg-white rounded-lg shadow-lg p-6 min-h-[80vh]">
            <div className="mb-6">
                <h2
                    style={{
                        color: "var(--navy)",
                        marginBottom: "20px",
                        fontSize: "large",
                        fontFamily: "large",
                    }}
                >
                    AFFILIATES MANAGEMENT
                </h2>
            </div>

            <section className="flex h-full flex-col rounded border border-gray-200 p-4">
                <div className="aqi-grid-toolbar">
                    <select
                        value={perPage}
                        onChange={(event) => {
                            setPerPage(Number(event.target.value));
                            setCurrentPage(1);
                        }}
                        className="aqi-grid-control rounded border-2 border-[#C2D7E2] bg-[#F5F7FA] px-3 font-medium text-navy focus:border-navy focus:outline-none"
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <div className="aqi-grid-search">
                        <FaSearch className="absolute left-3 top-3 text-navy" />
                        <input
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search partners..."
                            className="aqi-grid-search-input w-full border-2 border-[#C2D7E2] rounded pl-10 pr-3 focus:outline-none focus:border-navy"
                        />
                    </div>
                    <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                        <button
                            onClick={openNewPartnerModal}
                            className="aqi-grid-button aqi-grid-button--primary"
                        >
                            <FaPlus />
                            Add Partner
                        </button>
                        <button
                            onClick={handleExportPartnersCSV}
                            className="aqi-grid-button aqi-grid-button--primary"
                            title="Export CSV"
                        >
                            Export
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-x-auto rounded border border-gray-200">
                    <table className="min-w-full text-sm">
                        <thead className="bg-navy text-white">
                            <tr>
                                <th className="text-left p-3">Image</th>
                                <th className="text-left p-3">Affiliate Name</th>
                                <th className="text-left p-3">Website</th>
                                <th className="text-left p-3">Status</th>
                                <th className="text-right p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td className="p-4" colSpan={5}>
                                        Loading partners...
                                    </td>
                                </tr>
                            ) : filteredPartners.length === 0 ? (
                                <tr>
                                    <td className="p-4" colSpan={5}>
                                        No affiliate partners found.
                                    </td>
                                </tr>
                            ) : (
                                paginatedPartners.map((partner) => (
                                    <tr
                                        key={partner.id}
                                        onClick={() => setPreviewPartner(partner)}
                                        className="cursor-pointer border-t hover:bg-[#f1f8ff]"
                                    >
                                        <td className="p-3">
                                            {partner.image ? (
                                                <img
                                                    src={getPreviewUrl(
                                                        partner.image,
                                                    )}
                                                    alt={partner.affiliateName}
                                                    className="h-12 w-12 rounded border border-gray-200 object-contain"
                                                />
                                            ) : (
                                                <div className="h-12 w-12 rounded border border-dashed border-gray-300 bg-gray-50" />
                                            )}
                                        </td>
                                        <td className="p-4 font-medium text-navy">
                                            {partner.affiliateName}
                                        </td>
                                        <td className="p-4">
                                            <a
                                                href={partner.websiteLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                                className="text-navy hover:underline"
                                            >
                                                {partner.websiteLink}
                                            </a>
                                        </td>
                                        <td className="p-4">
                                            <span
                                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                                    partner.status === "draft"
                                                        ? "bg-yellow-100 text-yellow-800"
                                                        : "bg-green-100 text-green-800"
                                                }`}
                                            >
                                                {partner.status === "draft"
                                                    ? "Draft"
                                                    : "Published"}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleSelect(partner);
                                                    }}
                                                    className="text-navy hover:text-navy-2"
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleDelete(partner);
                                                    }}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <CustomPagination />
            </section>

            {previewPartner && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-6">
                    <section className="flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col rounded-lg bg-white p-5 shadow-2xl">
                        <div className="mb-4 flex items-start justify-between gap-4 border-b border-gray-200 pb-3">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg font-semibold text-navy">
                                    {previewPartner.affiliateName}
                                </h2>
                                {previewPartner.websiteLink && (
                                    <a
                                        href={previewPartner.websiteLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-1 block break-words text-sm text-navy hover:underline"
                                    >
                                        {previewPartner.websiteLink}
                                    </a>
                                )}
                                <span
                                    className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                        previewPartner.status === "draft"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : "bg-green-100 text-green-800"
                                    }`}
                                >
                                    {previewPartner.status === "draft"
                                        ? "Draft"
                                        : "Published"}
                                </span>
                            </div>
                            <button
                                onClick={() => setPreviewPartner(null)}
                                className="mt-0.5 inline-flex shrink-0 items-center gap-2 text-gray-600 hover:text-gray-900"
                                title="Close"
                            >
                                <FaTimes />
                                
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2">
                            {previewPartner.image && (
                                <div className="mb-4 flex justify-center rounded border border-gray-200 bg-gray-50 p-4">
                                    <img
                                        src={getPreviewUrl(previewPartner.image)}
                                        alt={previewPartner.affiliateName}
                                        className="h-32 w-32 object-contain"
                                    />
                                </div>
                            )}

                            <div>
                                <span className="mb-1 block text-sm font-medium text-gray-700">
                                    Description
                                </span>
                                <div className="whitespace-pre-wrap rounded border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-800">
                                    {previewPartner.description || "-"}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            )}

            {isEditorOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-6">
                    <section className="flex max-h-[calc(100vh-3rem)] w-full max-w-4xl flex-col rounded-lg bg-white p-5 shadow-2xl">
                        <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
                            <h2 className="text-lg font-semibold text-navy">
                                {selectedPartner
                                    ? "Edit Affiliate Partner"
                                    : "New Affiliate Partner"}
                            </h2>
                            <button
                                onClick={closeEditorModal}
                                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                                title="Close"
                            >
                                <FaTimes />
                                Close
                            </button>
                        </div>

                        <div className="grid flex-1 gap-4 overflow-y-auto pr-2 md:grid-cols-2">
                            <label className="md:col-span-2">
                                <span className="mb-1 block text-sm font-medium text-gray-700">
                                    Affiliate Name
                                </span>
                                <input
                                    name="affiliateName"
                                    value={form.affiliateName}
                                    onChange={handleChange}
                                    className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none focus:border-navy"
                                />
                            </label>

                            <label className="md:col-span-2">
                                <span className="mb-1 block text-sm font-medium text-gray-700">
                                    Description
                                </span>
                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={5}
                                    className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none focus:border-navy"
                                />
                            </label>

                            <label>
                                <span className="mb-1 block text-sm font-medium text-gray-700">
                                    Website Link
                                </span>
                                <input
                                    name="websiteLink"
                                    value={form.websiteLink}
                                    onChange={handleChange}
                                    placeholder="https://example.com"
                                    className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none focus:border-navy"
                                />
                            </label>

                            <label>
                                <span className="mb-1 block text-sm font-medium text-gray-700">
                                    Status
                                </span>
                                <select
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                    className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none focus:border-navy"
                                >
                                    <option value="published">Published</option>
                                    <option value="draft">Draft</option>
                                </select>
                            </label>

                            <div>
                                <span className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700">
                                    Image
                                    {renderImageSizeTooltip(
                                        AFFILIATE_IMAGE_SIZE_LABEL,
                                    )}
                                </span>
                                <label className="inline-flex cursor-pointer items-center gap-2 rounded bg-navy px-3 py-2 text-sm text-white hover:bg-navy-2">
                                    <FaUpload />
                                    {uploadingImage ? "Uploading..." : "Upload Image"}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        disabled={uploadingImage}
                                    />
                                </label>
                            </div>

                            {form.image && (
                                <div className="md:col-span-2">
                                    <span className="mb-1 block text-sm font-medium text-gray-700">
                                        Image Preview
                                    </span>
                                    <img
                                        src={getPreviewUrl(form.image)}
                                        alt="Affiliate preview"
                                        className="h-28 max-w-xs rounded border border-gray-200 object-contain"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mt-5 flex justify-end gap-3 border-t border-gray-200 pt-4">
                            <button
                                onClick={closeEditorModal}
                                className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="aqi-primary-action rounded bg-navy px-4 py-2 text-white hover:bg-navy-2 disabled:opacity-60"
                            >
                                {saving ? "Saving..." : "Save Partner"}
                            </button>
                        </div>
                    </section>
                </div>
            )}

            {cropImage && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-6">
                    <section className="flex h-[560px] w-full max-w-3xl flex-col rounded-lg bg-white p-5 shadow-2xl">
                        <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
                            <h2 className="text-lg font-semibold text-navy">
                                Crop Affiliate Image
                            </h2>
                            <button
                                onClick={handleCropCancel}
                                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                                title="Close"
                            >
                                <FaTimes />
                                Close
                            </button>
                        </div>

                        <div className="relative flex-1 overflow-hidden rounded-lg bg-gray-900">
                            <Cropper
                                image={cropImage}
                                crop={crop}
                                zoom={zoom}
                                aspect={AFFILIATE_IMAGE_ASPECT}
                                minZoom={1}
                                maxZoom={3}
                                restrictPosition
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(_area, croppedPixels) =>
                                    setCroppedAreaPixels(croppedPixels)
                                }
                            />
                        </div>

                        <p className="mt-3 text-sm text-gray-600">
                            Adjust the logo inside the fixed square frame used
                            on the public partners display.
                        </p>

                        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <label className="flex items-center gap-3 text-sm text-gray-700">
                                Zoom
                                <input
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.05}
                                    value={zoom}
                                    onChange={(event) =>
                                        setZoom(Number(event.target.value))
                                    }
                                    className="w-48 accent-navy"
                                />
                            </label>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={handleCropCancel}
                                    className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCropSave}
                                    className="aqi-primary-action rounded bg-navy px-4 py-2 text-white hover:bg-navy-2"
                                >
                                    Save Crop
                                </button>
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default AffiliatePartnerManagement;
